/**
 * Trader Data Context
 *
 * Provides real-time strategy data for the trader dashboard via polling.
 * Fetches strategy information from the trader API endpoints.
 */

/* eslint-disable react-refresh/only-export-components */
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
  ReactNode,
} from "react";
import { useLocation } from "react-router-dom";
import type {
  StrategyListItemV2,
  StrategiesListV2Response,
  PauseStrategyResponse,
  CompletedStrategyListItemV2,
  CompletedStrategiesListV2Response,
  ProfitTier,
} from "../types/strategy";
import { getApiConfigCached } from "../config/api.config";
import {
  buildTraderApiBasePath,
  getTraderRouteVersion,
} from "../utils/traderRouting";

/**
 * Default polling interval (3 seconds)
 */
const DEFAULT_POLL_INTERVAL_MS = 3000;

/**
 * Update budget request parameters
 */
interface UpdateBudgetParams {
  totalBudgetUsdt: number;
  entryDifferenceRatio?: number; // Optional - keeps current threshold if not provided
}

export const buildUpdateEntryThresholdRequest = (
  entryDifferenceRatio: number,
) => ({ entry_difference_ratio: entryDifferenceRatio });

export async function requestCompletedStrategiesV2(
  apiBaseUrl: string,
): Promise<CompletedStrategiesListV2Response> {
  const response = await fetch(`${apiBaseUrl}/strategies/completed/v2`);

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Bulk operation result for a single strategy
 */
export interface BulkOperationResult {
  strategy_id: string;
  success: boolean;
  error?: string;
}

/**
 * Bulk operation response
 */
export interface BulkOperationResponse {
  status: "ok" | "partial";
  total: number;
  succeeded: number;
  failed: number;
  results: BulkOperationResult[];
}

/**
 * Trader Data Context Interface
 */
interface TraderDataContextType {
  // Active strategies data
  strategies: StrategyListItemV2[];
  count: number;
  loading: boolean;
  error: string | null;
  lastUpdate: number;

  // Completed strategies data
  completedStrategies: CompletedStrategyListItemV2[];
  completedCount: number;
  completedUnavailableCount: number;
  completedLoading: boolean;
  completedError: string | null;
  completedLastUpdate: number;

  // Metadata
  traderName: string | null;

  // Active strategy actions
  refreshStrategies: () => Promise<void>;
  pauseStrategy: (strategyId: string) => Promise<boolean>;
  emergencyClearStrategy: (
    strategyId: string,
    options?: {
      keepReplay?: boolean;
    },
  ) => Promise<boolean>;
  getStrategy: (strategyId: string) => StrategyListItemV2 | undefined;

  // New strategy action methods (per strategy_actions.md)
  pauseMonitoring: (strategyId: string) => Promise<boolean>;
  reloadStrategy: (strategyId: string) => Promise<boolean>;
  updateBudget: (
    strategyId: string,
    params: UpdateBudgetParams,
  ) => Promise<boolean>;
  updateEntryThreshold: (
    strategyId: string,
    entryDifferenceRatio: number,
  ) => Promise<boolean>;
  updateProfitTiers: (
    strategyId: string,
    profitTiers: ProfitTier[],
  ) => Promise<boolean>;
  updateReplaySettings: (
    strategyId: string,
    replayEnabled: boolean,
    replayDelaySeconds: number,
  ) => Promise<boolean>;
  updateHedgeConfig: (
    strategyId: string,
    leverage?: number,
    rebalancing?: {
      price_surge_threshold?: number;
      liquidation_proximity_threshold?: number;
      entry_funding_rate_threshold?: number | null;
      entry_funding_interval_hours?: number;
      spot_futures_gap_magnitude_ratio?: number;
      low_funding_rate_threshold?: number;
      funding_time_window_seconds?: number;
    },
  ) => Promise<boolean>;

  skipStrategy: (strategyId: string) => Promise<boolean>;
  excludeStrategy: (strategyId: string) => Promise<boolean>;
  recoverMissingOrders: (strategyId: string) => Promise<boolean>;

  // Completed strategy actions
  refreshCompletedStrategies: () => Promise<void>;
  getCompletedStrategy: (
    strategyId: string,
  ) => CompletedStrategyListItemV2 | undefined;
  repairStrategy: (strategyId: string) => Promise<boolean>;

  // Bulk operations
  bulkPause: (strategyIds: string[]) => Promise<BulkOperationResponse>;
  bulkResume: (strategyIds: string[]) => Promise<BulkOperationResponse>;
  bulkReload: (strategyIds: string[]) => Promise<BulkOperationResponse>;
  bulkUpdateBudget: (
    strategyIds: string[],
    totalBudgetUsdt: number,
    entryDifferenceRatio?: number,
  ) => Promise<BulkOperationResponse>;
  bulkUpdateProfitTiers: (
    strategyIds: string[],
    profitTiers: ProfitTier[],
  ) => Promise<BulkOperationResponse>;
  bulkUpdateHedgeConfig: (
    strategyIds: string[],
    leverage?: number,
    rebalancing?: {
      price_surge_threshold?: number;
      liquidation_proximity_threshold?: number;
      entry_funding_rate_threshold?: number | null;
      entry_funding_interval_hours?: number;
      spot_futures_gap_magnitude_ratio?: number;
      low_funding_rate_threshold?: number;
      funding_time_window_seconds?: number;
    },
  ) => Promise<BulkOperationResponse>;

  // Configuration
  setPollInterval: (ms: number) => void;
  setEnabled: (enabled: boolean) => void;
}

const TraderDataContext = createContext<TraderDataContextType | undefined>(
  undefined,
);

/**
 * Trader Data Provider Props
 */
interface TraderDataProviderProps {
  children: ReactNode;
  traderName: string;
  pollInterval?: number;
  enabled?: boolean;
}

/**
 * Trader Data Provider Component
 *
 * Manages strategy data fetching and polling:
 * - Polls the trader API for strategy updates
 * - Provides actions for pausing strategies
 * - Tracks loading/error states
 */
export const TraderDataProvider: React.FC<TraderDataProviderProps> = ({
  children,
  traderName,
  pollInterval: initialPollInterval = DEFAULT_POLL_INTERVAL_MS,
  enabled: initialEnabled = true,
}) => {
  const location = useLocation();

  // Active strategies state
  const [strategies, setStrategies] = useState<StrategyListItemV2[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState(0);

  // Completed strategies state
  const [completedStrategies, setCompletedStrategies] = useState<
    CompletedStrategyListItemV2[]
  >([]);
  const [completedCount, setCompletedCount] = useState(0);
  const [completedUnavailableCount, setCompletedUnavailableCount] = useState(0);
  const [completedLoading, setCompletedLoading] = useState(true);
  const [completedError, setCompletedError] = useState<string | null>(null);
  const [completedLastUpdate, setCompletedLastUpdate] = useState(0);

  // Configuration state
  const [pollInterval, setPollInterval] = useState(initialPollInterval);
  const [enabled, setEnabled] = useState(initialEnabled);

  // Ref to store the polling interval ID
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);

  /**
   * Get the base API URL for trader endpoints
   */
  const getApiBaseUrl = useCallback(() => {
    const apiConfig = getApiConfigCached();
    const apiBasePath = buildTraderApiBasePath(
      traderName,
      getTraderRouteVersion(location.pathname),
    );
    // In production: use full URL with configured host
    // In development: use same port as WebSocket (8765)
    if (apiConfig.isProduction) {
      return `${window.location.protocol}//${apiConfig.host}${apiBasePath}`;
    } else {
      // Development: use WebSocket port (server serves both HTTP and WS on same port)
      const port = apiConfig.wsPort || 8765;
      return `http://${apiConfig.host}:${port}${apiBasePath}`;
    }
  }, [location.pathname, traderName]);

  /**
   * Fetch all strategies for the trader
   */
  const fetchStrategies = useCallback(async () => {
    if (!enabled || !traderName) return;

    try {
      const response = await fetch(`${getApiBaseUrl()}/strategies/v2`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data: StrategiesListV2Response = await response.json();

      if (isMountedRef.current) {
        setStrategies(data.strategies);
        setCount(data.count);
        setError(null);
        setLastUpdate(Date.now());
        setLoading(false);
      }
    } catch (err) {
      if (isMountedRef.current) {
        const errorMessage =
          err instanceof Error ? err.message : "Unknown error";
        setError(errorMessage);
        console.error(
          "[TraderDataContext] Failed to fetch strategies:",
          errorMessage,
        );
        setLoading(false);
      }
    }
  }, [enabled, traderName, getApiBaseUrl]);

  /**
   * Refresh strategies manually
   */
  const refreshStrategies = useCallback(async () => {
    setLoading(true);
    await fetchStrategies();
  }, [fetchStrategies]);

  /**
   * Pause a strategy
   */
  const pauseStrategy = useCallback(
    async (strategyId: string): Promise<boolean> => {
      try {
        const response = await fetch(
          `${getApiBaseUrl()}/strategies/${strategyId}/pause`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
          },
        );

        const data: PauseStrategyResponse = await response.json();

        if (data.status === "ok") {
          // Refresh strategies after pause
          await fetchStrategies();
          return true;
        } else {
          console.error(
            "[TraderDataContext] Failed to pause strategy:",
            data.message,
          );
          return false;
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Unknown error";
        console.error(
          "[TraderDataContext] Failed to pause strategy:",
          errorMessage,
        );
        return false;
      }
    },
    [getApiBaseUrl, fetchStrategies],
  );

  /**
   * Emergency clear a strategy (liquidate all positions)
   *
   * This triggers the emergency clear state which will:
   * - Sell all remaining spot holdings at market price
   * - Close the futures position at market price
   */
  const emergencyClearStrategy = useCallback(
    async (
      strategyId: string,
      options?: {
        keepReplay?: boolean;
      },
    ): Promise<boolean> => {
      try {
        const response = await fetch(
          `${getApiBaseUrl()}/strategies/${strategyId}/emergency_clear`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              keep_replay: options?.keepReplay,
            }),
          },
        );

        const data: PauseStrategyResponse = await response.json();

        if (data.status === "ok") {
          // Refresh strategies after emergency clear
          await fetchStrategies();
          return true;
        } else {
          console.error(
            "[TraderDataContext] Failed to emergency clear strategy:",
            data.message,
          );
          return false;
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Unknown error";
        console.error(
          "[TraderDataContext] Failed to emergency clear strategy:",
          errorMessage,
        );
        return false;
      }
    },
    [getApiBaseUrl, fetchStrategies],
  );

  /**
   * Get a specific strategy by ID
   */
  const getStrategy = useCallback(
    (strategyId: string): StrategyListItemV2 | undefined => {
      return strategies.find((s) => s.strategy_id === strategyId);
    },
    [strategies],
  );

  /**
   * Toggle monitoring pause for a strategy
   *
   * This pauses/resumes orderbook monitoring within the executor's loop
   * without changing the strategy state.
   */
  const pauseMonitoring = useCallback(
    async (strategyId: string): Promise<boolean> => {
      try {
        const response = await fetch(
          `${getApiBaseUrl()}/strategies/${strategyId}/pause`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
          },
        );

        const data: PauseStrategyResponse = await response.json();

        if (data.status === "ok") {
          // Refresh strategies after pause toggle
          await fetchStrategies();
          return true;
        } else {
          console.error(
            "[TraderDataContext] Failed to toggle monitoring pause:",
            data.message,
          );
          return false;
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Unknown error";
        console.error(
          "[TraderDataContext] Failed to toggle monitoring pause:",
          errorMessage,
        );
        return false;
      }
    },
    [getApiBaseUrl, fetchStrategies],
  );

  /**
   * Reload strategy snapshot from storage
   *
   * Signals the executor to reload the persisted state.
   * Useful after manually editing the snapshot file.
   */
  const reloadStrategy = useCallback(
    async (strategyId: string): Promise<boolean> => {
      try {
        const response = await fetch(
          `${getApiBaseUrl()}/strategies/${strategyId}/reload`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
          },
        );

        const data: PauseStrategyResponse = await response.json();

        if (data.status === "ok") {
          // Refresh strategies after reload
          await fetchStrategies();
          return true;
        } else {
          console.error(
            "[TraderDataContext] Failed to reload strategy:",
            data.message,
          );
          return false;
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Unknown error";
        console.error(
          "[TraderDataContext] Failed to reload strategy:",
          errorMessage,
        );
        return false;
      }
    },
    [getApiBaseUrl, fetchStrategies],
  );

  const skipStrategy = useCallback(
    async (strategyId: string): Promise<boolean> => {
      try {
        const response = await fetch(
          `${getApiBaseUrl()}/strategies/${strategyId}/skip`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
          },
        );

        const data = await response.json();

        if (data.status === "ok") {
          await fetchStrategies();
          return true;
        } else {
          console.error(
            "[TraderDataContext] Failed to skip strategy:",
            data.message,
          );
          return false;
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Unknown error";
        console.error(
          "[TraderDataContext] Failed to skip strategy:",
          errorMessage,
        );
        return false;
      }
    },
    [getApiBaseUrl, fetchStrategies],
  );

  /**
   * Update budget for a strategy
   *
   * Updates the opportunity's budget (max_profitable_volume) and optionally
   * the entry threshold (quote_conversion_rate). If in Standby state, the
   * strategy will transition to BuyOpen to use the new budget.
   */
  const updateBudget = useCallback(
    async (
      strategyId: string,
      params: UpdateBudgetParams,
    ): Promise<boolean> => {
      try {
        const response = await fetch(
          `${getApiBaseUrl()}/strategies/${strategyId}/update-budget`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              total_budget_usdt: params.totalBudgetUsdt,
              entry_difference_ratio: params.entryDifferenceRatio,
            }),
          },
        );

        const data = await response.json();

        if (data.status === "ok") {
          // Refresh strategies after budget update
          await fetchStrategies();
          return true;
        } else {
          console.error(
            "[TraderDataContext] Failed to update budget:",
            data.message,
          );
          return false;
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Unknown error";
        console.error(
          "[TraderDataContext] Failed to update budget:",
          errorMessage,
        );
        return false;
      }
    },
    [getApiBaseUrl, fetchStrategies],
  );

  const updateEntryThreshold = useCallback(
    async (
      strategyId: string,
      entryDifferenceRatio: number,
    ): Promise<boolean> => {
      try {
        const response = await fetch(
          `${getApiBaseUrl()}/strategies/${strategyId}/update-entry-threshold`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(
              buildUpdateEntryThresholdRequest(entryDifferenceRatio),
            ),
          },
        );
        const data = await response.json();
        if (data.status !== "ok") return false;
        await fetchStrategies();
        return true;
      } catch (error) {
        console.error(
          "[TraderDataContext] Failed to update entry threshold:",
          error,
        );
        return false;
      }
    },
    [getApiBaseUrl, fetchStrategies],
  );

  /**
   * Update profit tiers for a strategy
   *
   * Updates the profit_tiers configuration in the execution context.
   * Only valid when the strategy is in Standby state.
   */
  const updateProfitTiers = useCallback(
    async (strategyId: string, profitTiers: ProfitTier[]): Promise<boolean> => {
      try {
        const response = await fetch(
          `${getApiBaseUrl()}/strategies/${strategyId}/update-profit-tiers`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              profit_tiers: profitTiers,
            }),
          },
        );

        const data = await response.json();

        if (data.status === "ok") {
          // Refresh strategies after profit tiers update
          await fetchStrategies();
          return true;
        } else {
          console.error(
            "[TraderDataContext] Failed to update profit tiers:",
            data.message,
          );
          return false;
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Unknown error";
        console.error(
          "[TraderDataContext] Failed to update profit tiers:",
          errorMessage,
        );
        return false;
      }
    },
    [getApiBaseUrl, fetchStrategies],
  );

  /**
   * Update replay settings for a strategy.
   */
  const updateReplaySettings = useCallback(
    async (
      strategyId: string,
      replayEnabled: boolean,
      replayDelaySeconds: number,
    ): Promise<boolean> => {
      try {
        const response = await fetch(
          `${getApiBaseUrl()}/strategies/${strategyId}/update-replay-settings`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              replay_enabled: replayEnabled,
              replay_delay_seconds: replayDelaySeconds,
            }),
          },
        );

        const data = await response.json();

        if (data.status === "ok") {
          await fetchStrategies();
          return true;
        } else {
          console.error(
            "[TraderDataContext] Failed to update replay settings:",
            data.message,
          );
          return false;
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Unknown error";
        console.error(
          "[TraderDataContext] Failed to update replay settings:",
          errorMessage,
        );
        return false;
      }
    },
    [getApiBaseUrl, fetchStrategies],
  );

  /**
   * Update hedge config for a hedged strategy
   *
   * Updates leverage and rebalancing configuration in the execution context.
   * Changes take effect immediately without requiring strategy reload.
   */
  const updateHedgeConfig = useCallback(
    async (
      strategyId: string,
      leverage?: number,
      rebalancing?: {
        price_surge_threshold?: number;
        liquidation_proximity_threshold?: number;
        entry_funding_rate_threshold?: number | null;
        entry_funding_interval_hours?: number;
        spot_futures_gap_magnitude_ratio?: number;
        low_funding_rate_threshold?: number;
        funding_time_window_seconds?: number;
      },
    ): Promise<boolean> => {
      try {
        const response = await fetch(
          `${getApiBaseUrl()}/strategies/${strategyId}/update-hedge-config`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              leverage,
              rebalancing,
            }),
          },
        );

        const data = await response.json();

        if (data.status === "ok") {
          // Refresh strategies after hedge config update
          await fetchStrategies();
          return true;
        } else {
          console.error(
            "[TraderDataContext] Failed to update hedge config:",
            data.message,
          );
          return false;
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Unknown error";
        console.error(
          "[TraderDataContext] Failed to update hedge config:",
          errorMessage,
        );
        return false;
      }
    },
    [getApiBaseUrl, fetchStrategies],
  );

  // ============================================
  // BULK OPERATIONS
  // ============================================

  /**
   * Helper to call bulk API endpoints
   */
  const callBulkApi = useCallback(
    async (
      endpoint: string,
      body: Record<string, unknown>,
    ): Promise<BulkOperationResponse> => {
      try {
        const response = await fetch(
          `${getApiBaseUrl()}/strategies/bulk/${endpoint}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(body),
          },
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data: BulkOperationResponse = await response.json();

        // Refresh strategies after bulk operation
        await fetchStrategies();

        return data;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Unknown error";
        console.error(
          `[TraderDataContext] Bulk ${endpoint} failed:`,
          errorMessage,
        );
        // Return error response
        return {
          status: "partial",
          total: 0,
          succeeded: 0,
          failed: 0,
          results: [],
        };
      }
    },
    [getApiBaseUrl, fetchStrategies],
  );

  /**
   * Bulk pause multiple strategies
   */
  const bulkPause = useCallback(
    async (strategyIds: string[]): Promise<BulkOperationResponse> => {
      return callBulkApi("pause", { strategy_ids: strategyIds });
    },
    [callBulkApi],
  );

  /**
   * Bulk resume (unpause) multiple strategies
   */
  const bulkResume = useCallback(
    async (strategyIds: string[]): Promise<BulkOperationResponse> => {
      return callBulkApi("resume", { strategy_ids: strategyIds });
    },
    [callBulkApi],
  );

  /**
   * Bulk reload multiple strategies
   */
  const bulkReload = useCallback(
    async (strategyIds: string[]): Promise<BulkOperationResponse> => {
      return callBulkApi("reload", { strategy_ids: strategyIds });
    },
    [callBulkApi],
  );

  /**
   * Bulk update budget for multiple strategies
   */
  const bulkUpdateBudget = useCallback(
    async (
      strategyIds: string[],
      totalBudgetUsdt: number,
      entryDifferenceRatio?: number,
    ): Promise<BulkOperationResponse> => {
      return callBulkApi("update-budget", {
        strategy_ids: strategyIds,
        total_budget_usdt: totalBudgetUsdt,
        entry_difference_ratio: entryDifferenceRatio,
      });
    },
    [callBulkApi],
  );

  /**
   * Bulk update profit tiers for multiple strategies
   */
  const bulkUpdateProfitTiers = useCallback(
    async (
      strategyIds: string[],
      profitTiers: ProfitTier[],
    ): Promise<BulkOperationResponse> => {
      return callBulkApi("update-profit-tiers", {
        strategy_ids: strategyIds,
        profit_tiers: profitTiers,
      });
    },
    [callBulkApi],
  );

  /**
   * Bulk update hedge config for multiple strategies
   */
  const bulkUpdateHedgeConfig = useCallback(
    async (
      strategyIds: string[],
      leverage?: number,
      rebalancing?: {
        price_surge_threshold?: number;
        liquidation_proximity_threshold?: number;
        entry_funding_rate_threshold?: number | null;
        entry_funding_interval_hours?: number;
        spot_futures_gap_magnitude_ratio?: number;
        low_funding_rate_threshold?: number;
        funding_time_window_seconds?: number;
      },
    ): Promise<BulkOperationResponse> => {
      return callBulkApi("update-hedge-config", {
        strategy_ids: strategyIds,
        leverage,
        rebalancing,
      });
    },
    [callBulkApi],
  );

  /**
   * Fetch completed strategies from the API
   */
  const fetchCompletedStrategies = useCallback(async () => {
    if (!enabled || !traderName) return;

    try {
      const data = await requestCompletedStrategiesV2(getApiBaseUrl());

      if (isMountedRef.current) {
        setCompletedStrategies(data.strategies);
        setCompletedCount(data.count);
        setCompletedUnavailableCount(data.unavailable_count);
        setCompletedError(null);
        setCompletedLastUpdate(Date.now());
        setCompletedLoading(false);
      }
    } catch (err) {
      if (isMountedRef.current) {
        const errorMessage =
          err instanceof Error ? err.message : "Unknown error";
        setCompletedError(errorMessage);
        console.error(
          "[TraderDataContext] Failed to fetch completed strategies:",
          errorMessage,
        );
        setCompletedLoading(false);
      }
    }
  }, [enabled, traderName, getApiBaseUrl]);

  /**
   * Refresh completed strategies manually
   */
  const refreshCompletedStrategies = useCallback(async () => {
    setCompletedLoading(true);
    await fetchCompletedStrategies();
  }, [fetchCompletedStrategies]);

  /**
   * Exclude a strategy from the active or completed dashboard lists.
   *
   * Active strategies must already be monitoring-paused before exclusion.
   */
  const excludeStrategy = useCallback(
    async (strategyId: string): Promise<boolean> => {
      try {
        const response = await fetch(
          `${getApiBaseUrl()}/strategies/${strategyId}/exclude`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
          },
        );

        const data = await response.json();

        if (data.status === "ok") {
          await Promise.all([fetchStrategies(), fetchCompletedStrategies()]);
          return true;
        } else {
          console.error(
            "[TraderDataContext] Failed to exclude strategy:",
            data.message,
          );
          return false;
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Unknown error";
        console.error(
          "[TraderDataContext] Failed to exclude strategy:",
          errorMessage,
        );
        return false;
      }
    },
    [getApiBaseUrl, fetchStrategies, fetchCompletedStrategies],
  );

  const recoverMissingOrders = useCallback(
    async (strategyId: string): Promise<boolean> => {
      try {
        const response = await fetch(
          `${getApiBaseUrl()}/strategies/${strategyId}/recover-missing-orders`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
          },
        );

        const data = await response.json();

        if (data.status === "ok") {
          await Promise.all([fetchStrategies(), fetchCompletedStrategies()]);
          return true;
        } else {
          console.error(
            "[TraderDataContext] Failed to recover missing orders:",
            data.message,
          );
          return false;
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Unknown error";
        console.error(
          "[TraderDataContext] Failed to recover missing orders:",
          errorMessage,
        );
        return false;
      }
    },
    [getApiBaseUrl, fetchStrategies, fetchCompletedStrategies],
  );

  /**
   * Get a specific completed strategy by ID
   */
  const getCompletedStrategy = useCallback(
    (strategyId: string): CompletedStrategyListItemV2 | undefined => {
      return completedStrategies.find((s) => s.strategy_id === strategyId);
    },
    [completedStrategies],
  );

  /**
   * Repair (reconcile) a completed strategy
   *
   * Triggers state reconciliation for a completed strategy to fix any
   * data inconsistencies (missing orders, incorrect P&L, etc.)
   */
  const repairStrategy = useCallback(
    async (strategyId: string): Promise<boolean> => {
      try {
        const response = await fetch(
          `${getApiBaseUrl()}/strategies/${strategyId}/repair`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
          },
        );

        const data = await response.json();

        if (data.status === "ok") {
          // Refresh completed strategies after repair
          await fetchCompletedStrategies();
          return true;
        } else {
          console.error(
            "[TraderDataContext] Failed to repair strategy:",
            data.message,
          );
          return false;
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Unknown error";
        console.error(
          "[TraderDataContext] Failed to repair strategy:",
          errorMessage,
        );
        return false;
      }
    },
    [getApiBaseUrl, fetchCompletedStrategies],
  );

  /**
   * Start polling effect
   */
  useEffect(() => {
    isMountedRef.current = true;

    // Initial fetch for both active and completed strategies
    if (enabled && traderName) {
      fetchStrategies();
      fetchCompletedStrategies();
    }

    // Set up polling (only for active strategies - completed strategies are fetched on demand)
    if (enabled && traderName && pollInterval > 0) {
      pollIntervalRef.current = setInterval(fetchStrategies, pollInterval);
    }

    // Cleanup
    return () => {
      isMountedRef.current = false;
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [
    enabled,
    traderName,
    pollInterval,
    fetchStrategies,
    fetchCompletedStrategies,
  ]);

  /**
   * Handle enabled state changes
   */
  useEffect(() => {
    if (!enabled) {
      // Clear active strategies data when disabled
      setStrategies([]);
      setCount(0);
      setError(null);
      setLoading(true);

      // Clear completed strategies data when disabled
      setCompletedStrategies([]);
      setCompletedCount(0);
      setCompletedUnavailableCount(0);
      setCompletedError(null);
      setCompletedLoading(true);

      // Stop polling
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    }
  }, [enabled]);

  const value: TraderDataContextType = {
    // Active strategies
    strategies,
    count,
    loading,
    error,
    lastUpdate,

    // Completed strategies
    completedStrategies,
    completedCount,
    completedUnavailableCount,
    completedLoading,
    completedError,
    completedLastUpdate,

    // Metadata
    traderName,

    // Active strategy actions
    refreshStrategies,
    pauseStrategy,
    emergencyClearStrategy,
    getStrategy,

    // New strategy action methods (per strategy_actions.md)
    pauseMonitoring,
    reloadStrategy,
    skipStrategy,
    excludeStrategy,
    recoverMissingOrders,
    updateBudget,
    updateEntryThreshold,
    updateProfitTiers,
    updateReplaySettings,
    updateHedgeConfig,

    // Completed strategy actions
    refreshCompletedStrategies,
    getCompletedStrategy,
    repairStrategy,

    // Bulk operations
    bulkPause,
    bulkResume,
    bulkReload,
    bulkUpdateBudget,
    bulkUpdateProfitTiers,
    bulkUpdateHedgeConfig,

    // Configuration
    setPollInterval,
    setEnabled,
  };

  return (
    <TraderDataContext.Provider value={value}>
      {children}
    </TraderDataContext.Provider>
  );
};

/**
 * Hook to use trader data context
 */
export function useTraderData(): TraderDataContextType {
  const context = useContext(TraderDataContext);
  if (context === undefined) {
    throw new Error("useTraderData must be used within a TraderDataProvider");
  }
  return context;
}

/**
 * Hook to get a specific active strategy
 */
export function useStrategy(
  strategyId: string,
): StrategyListItemV2 | undefined {
  const { getStrategy } = useTraderData();
  return getStrategy(strategyId);
}

/**
 * Hook to get a specific completed strategy
 */
export function useCompletedStrategy(
  strategyId: string,
): CompletedStrategyListItemV2 | undefined {
  const { getCompletedStrategy } = useTraderData();
  return getCompletedStrategy(strategyId);
}

export default TraderDataContext;
